import { Grid } from "@mui/material";
import Aspera, { HstDataTransfer } from "csi/comm/Aspera";
import InvalidAttachmentDialog from "csi/comments/InvalidAttachmentDialog";
import Button from "designSystem/components/button/Button";
import File from "designSystem/components/file/File";
import html from "csi/html";
import { getAsperaFileData, getFileData, validateFiles } from "csi/service/fileService";
import { routerStore } from "csi/state/rootStore";
import { IFileData } from "csi/typings";
import { RegisterCommand } from "froala-editor";
import _ from "lodash";
import React, { Fragment, FunctionComponent, useRef, useState } from "react";
import FroalaEditor from "react-froala-wysiwyg";
import { ButtonEnum } from "designSystem/types/button-types";
import ModalDialog, { ModalDialogProps } from "../dialog/ModalDialog";
import { useLocale } from "designSystem/locale";
import { IUseUserProvider } from "designSystem/user";
import { ButtonIcon, BUTTON_ATTACH, commandFont, commandFormat, commandOther, defaultConfig } from "./config";
import { FroalaEditorComponentProps, moveCursorToEnd, selectTemplate } from "./FroalaEditorComponent";
import tributeMapping, { MentionUser } from "./tributeService";
import useMentionUser from "./useMentionUser";

export interface FroalaDialogProps extends Omit<ModalDialogProps, "children"> {
	/** default files when load the first time */
	defaultFiles?: IFileData[];

	/** other properties */
	editorProps?: Omit<FroalaEditorComponentProps, "configProps">;

	/** event handler for cancel button */
	onCancel?: () => void;

	/** placeholder for the editor */
	placeholderText?: string;

	/** userProvider for user search query */
	userProvider: IUseUserProvider<MentionUser>;

	/** push the content out, including text and files */
	pushContent: (content: string, addedFiles: IFileData[], deletedFiles: IFileData[], asperaData?: HstDataTransfer[]) => void;
}

const EDITOR_WIDTH = 770;

const FroalaDialog: FunctionComponent<FroalaDialogProps> = (props) => {
	const ref = useRef<FroalaEditor>();

	const {
		defaultFiles,
		onCancel,
		onClose,
		placeholderText,
		pushContent,
		editorProps,
		userProvider,
		...otherProps
	} = props;

	const [addedFiles, setAddedFiles] = useState<IFileData[]>(defaultFiles);
	const [deletedFiles, setDeletedFiles] = useState<IFileData[]>([]);
	const [asperaData, setAsperaData] = useState<HstDataTransfer[]>([]);
	const loc = useLocale();

	RegisterCommand(BUTTON_ATTACH, {
		callback: async () => {
			if (Aspera.isEnabled()) {
				const data = await Aspera.selectFile();
				setAsperaData([...asperaData, data]);
				setAddedFiles([...addedFiles, ...data.fileNames.map(getAsperaFileData)]);
			} else {
				const files = await html.selectFiles();

				try {
					await validateFiles(files);

					// attach the files
					setAddedFiles([...addedFiles, ...files.map(getFileData)]);
				} catch (errorFileNames) {
					routerStore.openDialog(InvalidAttachmentDialog, { errorFileNames });
				}
			}
		},
		focus: false,
		icon: ButtonIcon.attach,
		undo: false,
	});

	// update the deleted ones
	const onDeleteAttachment = (fileData: IFileData) => {
		setAddedFiles(_.difference(addedFiles, [fileData]));
		if (_.includes(defaultFiles, fileData)) {
			setDeletedFiles([...deletedFiles, fileData]);
		}
	};


	const value = editorProps?.value;
	const [content, setContent] = useState<string>(value ?? "");

	// confirm the input content
	const onConfirm = () => {
		pushContent(content, addedFiles, deletedFiles, asperaData);

		// close the dialog after confirm
		onClose?.();
	};

	// close the dialog
	const cancel = () => {
		onCancel?.();
		onClose?.();
	};

	const [setUserSearchQuery, setUserCallback] = useMentionUser([], userProvider.searchWithCache);

	const initializeEditor = () => {
		userProvider.clearSearchCache();
		if (value) {
			moveCursorToEnd(ref.current);
		}
		tributeMapping(ref, setUserSearchQuery, setUserCallback, selectTemplate, loc);
	};

	const editor = (
		<Grid
			container
			direction="row"
			item
			spacing={1}
			style={{ width: EDITOR_WIDTH }}
		>
			<Grid
				item
				style={{ marginBottom: 4 }}
				xs={12}
			>
				<FroalaEditor
					config={{
						...defaultConfig,
						events: { initialized: initializeEditor },
						heightMax: 215,
						heightMin: 215,
						placeholderText,
						toolbarButtons: {
							moreMisc: {
								buttons: [...commandFont, ...commandFormat, ...commandOther],
								buttonsVisible: 19,
							},

							// have to specify this so that "paragraphFormat" and other buttons become available
							moreParagraph: {
								buttons: [],
							},
						},
						width: EDITOR_WIDTH,
						// As per the suggestion from Froala, do this so that the popups and other UX helpers appear on top of other elements from the page.
						zIndex: 2501,
						...editorProps,
					}}
					model={content}
					onModelChange={setContent}
					ref={ref}
				/>
			</Grid>
			{addedFiles.map((fileData) => (
				<Grid
					item
					key={fileData.url}
					xs={6}
				>
					<File
						fileData={fileData}
						fileNameGrow={false}
						onDelete={!Aspera.isEnabled() ? () => onDeleteAttachment(fileData) : null}
					/>
				</Grid>
			))}
		</Grid>
	);

	const buttons = (
		<Fragment>
			<Button
				autoFocus
				design="primary"
				onClick={onConfirm}
				res={ButtonEnum.BUTTON_OK}
			/>
			<Button
				design="link"
				onClick={cancel}
				res="Cancel"
			/>
		</Fragment>
	);

	return (
		<ModalDialog
			maxWidth="md"
			showDividers
			{...otherProps}
		>
			{{
				main: editor,
				buttons,
			}}
		</ModalDialog>
	);
};

export default FroalaDialog;